import FootTrafficFilterApi from '@/Enums/Attribution/FootTraffic/FilterApi.enum';
import FootTrafficHour from '@/Enums/Attribution/FootTraffic/Hour.enum';
import ChartMetric from '@/Enums/Chart/Metric.enum';
import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { BeeswaxReportProperties as ReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import TotalMetric from '@/Enums/ReportingDashboard/TotalMetric.enum';
import type {
  IFootTrafficHydratedDateReportRecord,
  IFootTrafficImpressionsReports,
  IFootTrafficImpressionsReportsTotal,
  IFootTrafficTotal,
  IFootTrafficVisitsHistoryRecord,
  IFootTrafficVisitsReports,
} from '@/Interfaces/Attribution/FootTraffic.interface';
import {
  buildHistoricalAvgByWeek,
  buildVisitsByWeek,
  priorYearWeekKey,
  weekKey,
} from '@/helpers/attribution/fiscalWeek';
import { twoYearBaselineChange } from '@/helpers/attribution/twoYearBaseline';
import type { FootTrafficImpressionMetric } from '@/types/Attribution/FootTrafficImpressionMetric.types';
import type { FootTrafficFilters } from '@/types/Attribution/filters.types';
import moment, { type Moment } from 'moment';

export default class FootTrafficService {
  // Import happens on a friday
  protected static readonly daysToFriday: number = 5;

  public static readonly channelsToImpressionMetrics: Record<FootTrafficFilterApi, FootTrafficImpressionMetric> = {
    [FootTrafficFilterApi.Omnichannel]: ReportProperties.FT_OMNICHANNEL_IMPRESSIONS,
    [FootTrafficFilterApi.Youtube]: ReportProperties.FT_YOUTUBE_IMPRESSIONS,
    [FootTrafficFilterApi.Audio]: ReportProperties.FT_AUDIO_IMPRESSIONS,
  };

  public static getMaxFootTrafficDate(): Moment {
    const today = moment().startOf('day');
    const thisFriday = moment().day(this.daysToFriday).startOf('day');

    if (today >= thisFriday) {
      return moment().startOf('week');
    }
    return moment().startOf('week').subtract({ week: 1 });
  }

  public static getDefaultImpressionsReports(): IFootTrafficImpressionsReports {
    return {
      dateReport: [],
      flightWindows: [],
      totals: {
        [ReportProperties.FOOT_TRAFFIC_VISITS]: 0,
        [ReportProperties.FT_AUDIO_IMPRESSIONS]: 0,
        [ReportProperties.FT_OMNICHANNEL_IMPRESSIONS]: 0,
        [ReportProperties.FT_YOUTUBE_IMPRESSIONS]: 0,
        [ReportProperties.VISITS_CONVERSION_RATE]: 0,
        [ReportProperties.LIFT_PERCENT]: null,
        [ReportProperties.ON_AIR_AVG_WEEKLY_VISITS]: null,
        [ReportProperties.OFF_AIR_AVG_WEEKLY_VISITS]: null,
      },
    };
  }

  public static getDefaultVisitsReports(): IFootTrafficVisitsReports {
    return {
      weekdayReport: { ...{} },
      hourReport: Object.fromEntries(
        Object.values(FootTrafficHour).map(hour => [hour, 0]),
      ) as IFootTrafficVisitsReports['hourReport'],
      visitsHistory: [],
      historicalAverage: 0,
      yoyPercentChange: null,
      dwellReport: { segments: {}, avgDwellTime: 0, medianDwellTime: 0 },
      freqReport: { segments: {}, avgFrequency: 0, avgWeeklyUniqueVisitors: 0, repeatVisitorPercent: 0 },
      tradeAreaReport: {
        home: { segments: {}, primaryRadiusMiles: null, primaryRadiusPercent: null },
        work: { segments: {}, primaryRadiusMiles: null, primaryRadiusPercent: null },
      },
      competitiveRanking: null,
    };
  }

  public static getDefaultFootTrafficFilter(): FootTrafficFilters {
    const dateRangeEnd = this.getMaxFootTrafficDate();
    return {
      dateRangeStart: dateRangeEnd.clone().subtract({ days: 29 }).format(DateFormat.Iso8601),
      dateRangeEnd: dateRangeEnd.format(DateFormat.Iso8601),
      dateRangeFormat: FilterDate.LAST_30_DAYS,
      whitelabels: [],
      clients: [],
      advertisers: [],
      campaigns: [],
      locations: [],
      lineItems: [],
    };
  }

  public static getTotals(
    totals: IFootTrafficImpressionsReportsTotal,
    channels: FootTrafficFilterApi[],
    visitsHistory: IFootTrafficVisitsReports['visitsHistory'],
    yoyPercentChange: IFootTrafficVisitsReports['yoyPercentChange'],
    freqReport: IFootTrafficVisitsReports['freqReport'],
    dateRangeStart: string,
    dateRangeEnd: string,
  ): IFootTrafficTotal {
    const impressions = channels.reduce(
      (acc, type) => (acc += (totals[FootTrafficService.channelsToImpressionMetrics[type]] as number) ?? 0),
      0,
    );
    const visits = totals[ReportProperties.FOOT_TRAFFIC_VISITS];
    return {
      [TotalMetric.Impressions]: impressions,
      [TotalMetric.FootTrafficVisits]: visits,
      [TotalMetric.FootTrafficLiftPercent]: totals[ReportProperties.LIFT_PERCENT],
      [TotalMetric.VisitsConversionRate]: totals[ReportProperties.VISITS_CONVERSION_RATE],
      [TotalMetric.YoyPercentChange]: yoyPercentChange,
      [TotalMetric.TwoYearBaselineChange]: twoYearBaselineChange(visitsHistory, dateRangeStart, dateRangeEnd),
      [TotalMetric.AvgWeeklyUniqueVisitors]: freqReport.avgWeeklyUniqueVisitors,
    };
  }

  public static getChartDisplayData(
    dateReport: IFootTrafficHydratedDateReportRecord[],
    channels: FootTrafficFilterApi[],
    visitsHistory: IFootTrafficVisitsHistoryRecord[] = [],
  ) {
    const data = dateReport?.filter(val => val[ReportProperties.DAY]);

    const dateObjects = data.map(dayData => {
      const [year, month, day] = dayData.Day.split('-').map(Number);
      return new Date(year, month - 1, day);
    });

    const sortedDates = dateObjects.map(date => moment(date).format(DateFormat.ShortMonthDay));

    const omnichannelImpressions = channels.includes(FootTrafficFilterApi.Omnichannel)
      ? data.map(dayData => dayData[ReportProperties.FT_OMNICHANNEL_IMPRESSIONS])
      : [];
    const youtubeImpressions = channels.includes(FootTrafficFilterApi.Youtube)
      ? data.map(dayData => dayData[ReportProperties.FT_YOUTUBE_IMPRESSIONS])
      : [];
    const audioImpressions = channels.includes(FootTrafficFilterApi.Audio)
      ? data.map(dayData => dayData[ReportProperties.FT_AUDIO_IMPRESSIONS])
      : [];
    const visits = data.map(dayData => dayData[ReportProperties.FOOT_TRAFFIC_VISITS]);

    const visitsByWeek = buildVisitsByWeek(visitsHistory);
    const historicalAvgByWeek = buildHistoricalAvgByWeek(visitsHistory);
    const priorYearVisits = data.map(d => visitsByWeek.get(priorYearWeekKey(d.Day)) ?? null);
    const historicalAvgVisits = data.map(d => historicalAvgByWeek.get(weekKey(d.Day)) ?? null);
    const hasPriorYearOverlay = priorYearVisits.some(v => v !== null);
    const hasHistoricalAvgOverlay = historicalAvgVisits.some(v => v !== null);

    return {
      sortedDates,
      omnichannelImpressions,
      youtubeImpressions,
      audioImpressions,
      visits,
      priorYearVisits,
      historicalAvgVisits,
      hasPriorYearOverlay,
      hasHistoricalAvgOverlay,
    };
  }

  /**Get date report csv export formatted data. Channel filters are not applied on purpose */
  public static getChartCsv(
    dateReport: IFootTrafficHydratedDateReportRecord[],
    visitsHistory: IFootTrafficVisitsHistoryRecord[] = [],
  ) {
    const data = (dateReport ?? []).filter(val => val[ReportProperties.DAY]);
    const visitsByWeek = buildVisitsByWeek(visitsHistory);
    const historicalAvgByWeek = buildHistoricalAvgByWeek(visitsHistory);

    return data.map(record => {
      const day = record[ReportProperties.DAY];
      const visits = record[ReportProperties.FOOT_TRAFFIC_VISITS] ?? 0;
      const priorYear = visitsByWeek.get(priorYearWeekKey(day)) ?? null;
      const yoyPercent = priorYear ? ((visits - priorYear) / priorYear) * 100 : null;
      return {
        Date: day,
        [ChartMetric.FootTrafficVisits]: visits,
        'Prior Year Visits': priorYear ?? '',
        '3-Year Avg Visits': historicalAvgByWeek.get(weekKey(day)) ?? '',
        'YoY %': yoyPercent === null ? '' : yoyPercent.toFixed(1),
        [ChartMetric.ImpressionsOmnichannel]: record[ReportProperties.FT_OMNICHANNEL_IMPRESSIONS] ?? 0,
        [ChartMetric.ImpressionsYoutube]: record[ReportProperties.FT_YOUTUBE_IMPRESSIONS] ?? 0,
        [ChartMetric.ImpressionsAudio]: record[ReportProperties.FT_AUDIO_IMPRESSIONS] ?? 0,
      };
    });
  }
}
